/**
 * Main Changelog — timeline + client-side product filters
 */
(function () {
  'use strict';

  var QUERY_KEY = 'product';
  var DESKTOP_MQ = '(min-width: 750px)';
  var activeInstance = null;

  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function getQueryProduct() {
    try {
      var params = new URLSearchParams(window.location.search);
      return (params.get(QUERY_KEY) || 'all').toLowerCase();
    } catch (e) {
      return 'all';
    }
  }

  function setQueryProduct(handle) {
    try {
      var url = new URL(window.location.href);
      if (!handle || handle === 'all') {
        url.searchParams.delete(QUERY_KEY);
      } else {
        url.searchParams.set(QUERY_KEY, handle);
      }
      window.history.replaceState({}, '', url.toString());
    } catch (e) {
      /* ignore */
    }
  }

  class MainChangelog {
    constructor(root) {
      this.root = root || document.querySelector('[data-changelog-root]');
      if (!this.root) return;

      this.entriesContainer = this.root.querySelector('[data-changelog-entries]');
      this.entries = Array.from(this.root.querySelectorAll('.changelog__entry'));
      this.filters = Array.from(this.root.querySelectorAll('[data-changelog-filter]'));
      this.statusEl = this.root.querySelector('[data-filter-status]');
      this.emptyEl = this.root.querySelector('[data-filter-empty]');
      this.timelineBar = this.root.querySelector('.changelog__timeline-bar');
      this.timelineProgress = this.root.querySelector('.changelog__timeline-progress');
      this.showingTemplate =
        (this.statusEl && this.statusEl.textContent) || 'Showing {{ count }} updates';

      this.mobileScrollHandler = null;
      this.activeEntries = new Set();
      this.timelineInitialized = false;
      this.timelineScrollHandler = null;
      this.timelineResizeHandler = null;
      this.resizeObserver = null;
      this.currentFilter = 'all';
      this.onFilterClick = this.handleFilterClick.bind(this);
      this.onPopState = this.handlePopState.bind(this);

      this.init();
    }

    init() {
      if (!this.entriesContainer) return;

      this.bindFilters();
      this.setupInitialState();
      this.initTimelineProgress();
      this.initMobileScrollEffect();

      var initial = getQueryProduct();
      if (!this.isValidFilter(initial)) initial = 'all';
      this.applyFilter(initial, { skipUrl: true, scrollTop: false });

      window.addEventListener('popstate', this.onPopState);
    }

    isValidFilter(handle) {
      if (handle === 'all') return true;
      return this.filters.some(function (btn) {
        return btn.getAttribute('data-filter-product') === handle;
      });
    }

    bindFilters() {
      var self = this;
      this.filters.forEach(function (btn) {
        btn.addEventListener('click', self.onFilterClick);
      });
    }

    handleFilterClick(event) {
      var btn = event.currentTarget;
      var handle = btn.getAttribute('data-filter-product') || 'all';
      this.applyFilter(handle, { skipUrl: false, scrollTop: true });
    }

    handlePopState() {
      var handle = getQueryProduct();
      if (!this.isValidFilter(handle)) handle = 'all';
      this.applyFilter(handle, { skipUrl: true, scrollTop: false });
    }

    applyFilter(handle, options) {
      options = options || {};
      this.currentFilter = handle || 'all';

      var visibleCount = 0;
      this.entries.forEach(function (entry) {
        var entryHandle = entry.getAttribute('data-product-handle') || '';
        var match = handle === 'all' || entryHandle === handle;
        entry.hidden = !match;
        if (match) visibleCount += 1;
      });

      this.syncFilterButtons(handle);
      this.updateStatus(visibleCount);
      this.toggleEmptyState(visibleCount === 0 && this.entries.length > 0);
      this.refreshGlassActivePill();

      if (!options.skipUrl) setQueryProduct(handle);

      if (options.scrollTop) {
        var y = this.root.getBoundingClientRect().top + window.scrollY - 80;
        window.scrollTo({
          top: Math.max(0, y),
          behavior: prefersReducedMotion() ? 'auto' : 'smooth',
        });
      }

      // Recalculate timeline after layout settles
      var self = this;
      requestAnimationFrame(function () {
        self.refreshTimeline();
      });
    }

    syncFilterButtons(handle) {
      this.filters.forEach(function (btn) {
        var isActive = btn.getAttribute('data-filter-product') === handle;
        btn.classList.toggle('is-active', isActive);
        btn.setAttribute('aria-pressed', isActive ? 'true' : 'false');
        if (btn.getAttribute('role') === 'tab') {
          btn.setAttribute('aria-selected', isActive ? 'true' : 'false');
        }
        if (isActive) {
          btn.setAttribute('data-orbe-glass', '');
        } else {
          btn.removeAttribute('data-orbe-glass');
          btn.removeAttribute('data-orbe-glass-tier');
          var surface = btn.querySelector('[data-orbe-glass-surface]');
          if (surface) surface.style.filter = '';
        }
      });
    }

    refreshGlassActivePill() {
      if (!window.OrbeGlass) return;
      this.filters.forEach(function (btn) {
        if (btn.classList.contains('is-active')) {
          btn.setAttribute('data-orbe-glass', '');
          if (window.OrbeGlass.destroy) window.OrbeGlass.destroy(btn);
          if (window.OrbeGlass.init) window.OrbeGlass.init(btn);
        } else if (window.OrbeGlass.destroy) {
          window.OrbeGlass.destroy(btn);
        }
      });
    }

    updateStatus(count) {
      if (!this.statusEl) return;
      var template =
        this.statusEl.getAttribute('data-template') ||
        'Showing __COUNT__ updates';
      this.statusEl.textContent = template.split('__COUNT__').join(String(count));
    }

    toggleEmptyState(show) {
      if (!this.emptyEl) return;
      this.emptyEl.hidden = !show;
      if (this.entriesContainer) {
        this.entriesContainer.hidden = show;
      }
      if (this.timelineBar) {
        this.timelineBar.hidden = show;
      }
    }

    setupInitialState() {
      if (prefersReducedMotion()) return;
      this.entries.forEach(function (entry, index) {
        entry.style.animationDelay = index * 0.1 + 's';
      });
    }

    getVisibleEntries() {
      return this.entries.filter(function (entry) {
        return !entry.hidden;
      });
    }

    initTimelineProgress() {
      var self = this;
      var handleTimelineState = function () {
        var isDesktop = window.matchMedia(DESKTOP_MQ).matches;
        if (isDesktop && !self.timelineInitialized) {
          self.initializeTimeline();
        } else if (!isDesktop && self.timelineInitialized) {
          self.destroyTimeline();
        } else if (isDesktop && self.timelineInitialized) {
          self.calculateTimelineHeight();
        }
      };

      this.timelineResizeHandler = handleTimelineState;
      handleTimelineState();
      window.addEventListener('resize', handleTimelineState);
    }

    refreshTimeline() {
      if (!window.matchMedia(DESKTOP_MQ).matches) return;
      if (!this.timelineInitialized) {
        this.initializeTimeline();
        return;
      }
      this.calculateTimelineHeight();
      if (this.timelineScrollHandler) this.timelineScrollHandler();
    }

    calculateTimelineHeight() {
      if (!this.timelineBar) return;
      var visible = this.getVisibleEntries();
      if (visible.length === 0) {
        this.timelineBar.style.height = '0px';
        if (this.timelineProgress) this.timelineProgress.style.height = '0%';
        return;
      }

      var firstDot = visible[0].querySelector('.changelog__entry-timeline-dot');
      var lastDot = visible[visible.length - 1].querySelector(
        '.changelog__entry-timeline-dot'
      );
      if (!firstDot || !lastDot) return;

      var firstDotTop = firstDot.getBoundingClientRect().top + window.scrollY;
      var lastDotTop = lastDot.getBoundingClientRect().top + window.scrollY;
      var firstDotCenter = firstDotTop + firstDot.offsetHeight / 2;
      var lastDotCenter = lastDotTop + lastDot.offsetHeight / 2;
      var timelineHeight = Math.max(0, lastDotCenter - firstDotCenter);
      this.timelineBar.style.height = timelineHeight + 'px';
    }

    initializeTimeline() {
      if (this.timelineInitialized || !this.timelineBar || !this.timelineProgress) return;
      this.timelineInitialized = true;

      var self = this;
      this.calculateTimelineHeight();
      setTimeout(function () {
        self.calculateTimelineHeight();
      }, 100);

      if (window.ResizeObserver) {
        this.resizeObserver = new ResizeObserver(function () {
          setTimeout(function () {
            self.calculateTimelineHeight();
          }, 50);
        });
        this.entries.forEach(function (entry) {
          self.resizeObserver.observe(entry);
        });
      }

      var updateProgress = function () {
        if (!self.timelineBar || !self.timelineProgress) return;
        var visible = self.getVisibleEntries();
        var scrollTop = window.scrollY + window.innerHeight / 2;
        var timelineTop = window.scrollY + self.timelineBar.getBoundingClientRect().top;
        var timelineHeight = self.timelineBar.clientHeight;
        var progressHeight;

        if (timelineHeight <= 0 || scrollTop < timelineTop) {
          progressHeight = 0;
        } else if (scrollTop > timelineHeight + timelineTop) {
          progressHeight = 100;
        } else {
          progressHeight = ((scrollTop - timelineTop) / timelineHeight) * 100;
        }

        self.timelineProgress.style.height = progressHeight + '%';

        visible.forEach(function (entry) {
          var dot = entry.querySelector('.changelog__entry-timeline-dot');
          if (!dot) return;
          var entryTop = window.scrollY + dot.getBoundingClientRect().top;
          if (entryTop < scrollTop) {
            entry.classList.add('active');
          } else {
            entry.classList.remove('active');
          }
        });

        // Clear active on hidden entries
        self.entries.forEach(function (entry) {
          if (entry.hidden) entry.classList.remove('active');
        });
      };

      this.timelineScrollHandler = updateProgress;
      updateProgress();
      window.addEventListener('scroll', updateProgress, { passive: true });
    }

    destroyTimeline() {
      if (!this.timelineInitialized) return;
      this.timelineInitialized = false;

      if (this.timelineScrollHandler) {
        window.removeEventListener('scroll', this.timelineScrollHandler);
        this.timelineScrollHandler = null;
      }

      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
        this.resizeObserver = null;
      }

      if (this.timelineBar) this.timelineBar.style.height = 'auto';
      if (this.timelineProgress) this.timelineProgress.style.height = '0%';

      this.entries.forEach(function (entry) {
        entry.classList.remove('active');
      });
    }

    initMobileScrollEffect() {
      if (window.matchMedia(DESKTOP_MQ).matches) return;

      var self = this;
      var updateMobileEntries = function () {
        if (window.matchMedia(DESKTOP_MQ).matches) {
          self.destroyMobileScrollEffect();
          return;
        }

        self.getVisibleEntries().forEach(function (entry) {
          var entryRect = entry.getBoundingClientRect();
          var viewportHeight = window.innerHeight;
          var isInViewport =
            entryRect.top < viewportHeight * 0.7 && entryRect.bottom > viewportHeight * 0.3;

          if (isInViewport) {
            if (!self.activeEntries.has(entry)) {
              entry.classList.add('mobile-active');
              self.activeEntries.add(entry);
            }
          } else if (self.activeEntries.has(entry)) {
            entry.classList.remove('mobile-active');
            self.activeEntries.delete(entry);
          }
        });
      };

      this.mobileScrollHandler = updateMobileEntries;
      updateMobileEntries();
      window.addEventListener('scroll', updateMobileEntries, { passive: true });
    }

    destroyMobileScrollEffect() {
      if (this.mobileScrollHandler) {
        window.removeEventListener('scroll', this.mobileScrollHandler);
        this.mobileScrollHandler = null;
      }
      this.entries.forEach(function (entry) {
        entry.classList.remove('mobile-active');
      });
      this.activeEntries.clear();
    }

    destroy() {
      var self = this;
      this.filters.forEach(function (btn) {
        btn.removeEventListener('click', self.onFilterClick);
      });
      window.removeEventListener('popstate', this.onPopState);
      if (this.timelineResizeHandler) {
        window.removeEventListener('resize', this.timelineResizeHandler);
      }
      this.destroyTimeline();
      this.destroyMobileScrollEffect();
    }

    refresh() {
      this.entries = Array.from(this.root.querySelectorAll('.changelog__entry'));
      this.filters = Array.from(this.root.querySelectorAll('[data-changelog-filter]'));
      this.setupInitialState();
      this.applyFilter(this.currentFilter || getQueryProduct(), {
        skipUrl: true,
        scrollTop: false,
      });
    }
  }

  function boot(scope) {
    if (activeInstance) {
      activeInstance.destroy();
      activeInstance = null;
    }
    var root =
      (scope && scope.querySelector && scope.querySelector('[data-changelog-root]')) ||
      document.querySelector('[data-changelog-root]');
    if (!root) return;
    activeInstance = new MainChangelog(root);
  }

  document.addEventListener('DOMContentLoaded', function () {
    boot(document);
  });

  document.addEventListener('shopify:section:load', function (event) {
    if (event.target && event.target.querySelector('.main-changelog-section, [data-changelog-root]')) {
      boot(event.target);
    }
  });

  window.MainChangelog = MainChangelog;
})();
